/**
 * Shared course content and constants.
 *
 * Kept in one place so the live page, the self-paced page and the challenge
 * pages cannot drift apart in wording, price framing or guarantee terms.
 */

/**
 * Preview video.
 *
 * Updated on 2026-09-04 to the focused Challenge class preview. It opens with
 * a real AI-assisted research workflow and then maps that demonstration to the
 * five-week Bootcamp, so a warm buyer can sample Rob's teaching and understand
 * the offer without sitting through the complete replay.
 */
export const PREVIEW_VIDEO_ID = 'E75Y61W9C7U';
export const PREVIEW_VIDEO_LENGTH = '18 minutes';
export const PREVIEW_VIDEO_DURATION_SECONDS = 1071;
export const PREVIEW_VIDEO_URL = `https://www.youtube.com/watch?v=${PREVIEW_VIDEO_ID}`;

/**
 * Bootcamp trailer (Rob, 2026-09-24: "please put this on our AI Bootcamp page").
 *
 * A 1:42 promo cut, unlisted on the Lumiwealth channel. It sits right under the
 * hero on every Bootcamp page arm and does not replace the 18-minute class
 * preview above, which is a different thing: a real, unedited lesson.
 *
 * Why a constant and not a database value: the `course` row
 * (botspot_node/src/entities/course.entity.ts) has no video or trailer column,
 * and every other page video already lives here.
 *
 * The poster is the trailer's own title card (18.5 s into the master), because
 * YouTube's auto-picked frame for this upload lands mid-sentence.
 */
export const TRAILER_VIDEO_ID = 'zGkh3gDL2uU';
export const TRAILER_VIDEO_DURATION_SECONDS = 102;
export const TRAILER_VIDEO_LENGTH_LABEL = '1:42';
export const TRAILER_POSTER_SRC = '/images/courses/bootcamp-trailer-poster.webp';
export const TRAILER_POSTER_SRC_SMALL = '/images/courses/bootcamp-trailer-poster-640.webp';

/** Guarantee window in days. Keep every course surface aligned to this value. */
export const GUARANTEE_DAYS = 30;

export const GUARANTEE_HEADLINE = `${GUARANTEE_DAYS}-day money-back guarantee`;
export const GUARANTEE_BODY = '30-day money-back guarantee.';

export const NO_CODING_BADGE = 'No coding experience required';

export const ROB_LINKEDIN_URL = 'https://www.linkedin.com/in/robertgrzesik/';

/** What a buyer has after the very first session. Attacks Hormozi's time-delay term. */
export const FIRST_NIGHT_PROMISE =
  'You will have a working AI trading workspace and a reviewed trade on night one.';

/**
 * Rob's story.
 *
 * Brunson's Star/Story/Solution. The vulnerability opener only works if it is
 * immediately followed by proof of competence. An earlier version stopped at
 * "I blew up my account in 2008" and never recovered, which read as "let me
 * teach you what I got wrong". Rob's note, 2026-08-04: if you are going to
 * show vulnerability you have to show the capability that came after it.
 *
 * Every credential here is from MarketingManager/docs/ROB-PROFILE-POSITIONING.md.
 * Do NOT invent or inflate. Specifically: he was an early Voyager employee and
 * Software Technical Lead, NOT a co-founder; Greystone is a commercial real
 * estate lender and the verifiable figure is $3B+ in transactions processed,
 * not AUM; Voyager's $1B+ is peak market cap, not assets.
 *
 * The family account is deliberately described without figures. Rob is not
 * certain of the exact numbers, and a specific multiple would be a performance
 * claim we cannot substantiate.
 */
/**
 * Instructor story for the live bootcamp sales page.
 * Facts must stay inside MarketingManager/docs/ROB-PROFILE-POSITIONING.md.
 * Never claim co-founder of Voyager, Wall Street, or Greystone AUM.
 */
export const ROB_STORY = {
  heading: 'Your instructor',
  roleLine: 'Rob Grzesik · CEO of BotSpot · Creator of Lumibot · Master of Finance, Schulich',
  credentials: [
    { value: '25+', label: 'years coding' },
    { value: '15+', label: 'years in fintech' },
    { value: '14,000+', label: 'students taught' },
    { value: '~2,000', label: 'Lumibot GitHub stars' },
    { value: '1,000+', label: 'trading systems built' },
    { value: '$1B+', label: 'Voyager peak market cap' },
  ],
  paragraphs: [
    "In 2008 I blew up my own trading account. I had conviction, I had opinions, and I had no way of knowing whether any of it was actually true before I put money behind it. So I stopped guessing. I taught myself to backtest everything, then earned a Master of Finance at the Schulich School of Business at York University. When my father passed away I took over my family's accounts, and that same discipline grew them many times over. That money is still working today, and it is why I take this seriously.",
    'After grad school I built a software company and sold it, which paid for a couple of years of travelling. Then I landed in New York at Greystone as VP of Fintech, where I built loan software that processed over $3 billion in transactions. After that I was one of the first engineers at Voyager Digital, building the trading and brokerage infrastructure from scratch and growing the engineering team at a company that reached a $1B+ peak market cap. I later served as VP of Software Engineering at Perch and have hired and managed 100+ engineers.',
    'Along the way I created Lumibot, now nearly 2,000 stars on GitHub, personally built more than 1,000 trading systems, and have taught algorithmic trading and AI trading to 14,000+ students over the last seven years. What changed recently is that AI agents got good enough to do work that used to need a desk of analysts: read the filings, pull live data, run the analysis, and prepare the trade. Almost nobody knows what these tools can actually do yet. That gap is the reason this bootcamp exists.',
  ],
};

/**
 * ============================================================================
 * WHAT THIS IS WORTH: outcome-based, not cost-substitution
 * ============================================================================
 *
 * The previous version of this section priced the bootcamp against five older
 * Lumiwealth courses. Rob killed it on 2026-08-04 and he was right: those
 * courses are dated, they are NOT included in the bootcamp, and calling them
 * "the same curriculum" was misleading. Pricing 15 hours of his time also
 * lands at roughly what the course costs, so it argues for nothing.
 *
 * What replaced it answers the question a buyer is actually asking: what does
 * this do for my life? Each pillar carries a real, published, linked figure
 * about THEM: their time, their skills, and their process. None of it is a
 * claim about investment returns, which we neither make nor can make.
 *
 * RULES:
 *   - Every number cites a real published source, linked, with a date.
 *   - Never a return, profit or performance claim, direct or implied.
 *   - Never price a line against a product the buyer does not receive.
 */

/** Date the external figures below were last verified. */
export const PRICES_VERIFIED_ON = '2026-08-04';

export const WORTH_HEADING = 'What is this actually worth to you?';
export const WORTH_LEDE =
  'No projections about what you will make, because nobody can honestly give you those. ' +
  'Here is what changes for you instead, and where each number comes from.';

export const WORTH_PILLARS = [
  {
    key: 'research-gap',
    img: '/images/courses/worth-research-gap.webp',
    stat: '6 minutes',
    statLabel: 'is what the median retail investor spends researching a stock before buying it',
    heading: 'You close the gap between the research you do and the research the decision deserves',
    body:
      "Six minutes is the median. Buffett's own answer for doing it properly is six to eight hours a " +
      'week per portfolio. Almost nobody has that, so almost everybody trades on a fraction of the ' +
      'homework the decision deserves. An agent that reads the filings, pulls the live data and shows ' +
      'you its sources closes that gap without asking for your evenings.',
    source:
      'Median research time: Forbes, on retail order-flow research (2025). Six to eight hours: Warren Buffett.',
    href: 'https://www.forbes.com/sites/boazsobrado/2025/05/30/6-minutes-of-retail-trader-research-drives-20-of-stock-market-volume/',
  },
  {
    key: 'time-back',
    img: '/images/courses/worth-time-back.webp',
    stat: '~1.5 hours',
    statLabel: 'a day of pre-market prep, collapsed to about ten minutes',
    heading: 'You get your mornings back',
    body:
      'That figure is not ours. It is what a trader reported in a post that 529 people upvoted: one to ' +
      'two hours of scanning tickers, news and sentiment every morning, down to roughly ten minutes ' +
      'once the AI does the pass first. Across a year of trading days that is hundreds of hours you ' +
      'are not spending in front of a screen.',
    source: 'r/Daytrading, "AI now saves me 2 hours every morning" (529 upvotes, 2025).',
    href: 'https://www.reddit.com/r/Daytrading/comments/1oeo3lg/',
  },
  {
    key: 'skill',
    img: '/images/courses/worth-skill.webp',
    stat: '+$18,000',
    statLabel: 'a year is the measured salary premium on job postings that ask for AI skills',
    heading: 'The skill is worth something whether or not you ever place a trade',
    body:
      'What you actually learn here is how to direct an AI agent to do real work and how to check it. ' +
      'the trading is the vehicle. Lightcast puts the premium on AI-skilled roles at 28%, about ' +
      '$18,000 a year, across 1.3 billion job postings. PwC, across a billion job ads in 27 countries, ' +
      'puts it at 62%. That is a recurring number against a one-time price.',
    source: 'Lightcast AI Skills Report (2025); PwC Global AI Jobs Barometer (2026).',
    href: 'https://www.prnewswire.com/news-releases/new-lightcast-report-ai-skills-command-28-salary-premium-as-demand-shifts-beyond-tech-industry-302511141.html',
  },
  {
    key: 'process',
    img: '/images/courses/worth-process.webp',
    stat: '1 decision',
    statLabel: 'is all it takes for a verification habit to pay for the whole thing',
    heading: 'You stop finding out you were wrong from the price',
    body:
      'Rob blew up an account in 2008 holding a conviction he had no way to test. The entire method here ' +
      'is built around making the AI show its evidence so you can catch it before you act, not after. ' +
      'We are not going to put a dollar figure on a mistake you did not make. You already know what ' +
      'one of those costs you.',
    source: null,
    href: null,
  },
];

/**
 * The cohort trading competition.
 *
 * Explain WHAT it is and WHY it exists (Rob, 2026-08-06): the whole class
 * competes on returns across the five weeks, every trade placed with AI.
 * Do not lead with mechanics like "optional" or "paper vs real money";
 * those details bored buyers and explained nothing.
 */
export const COMPETITION = {
  name: 'Trading competition',
  stackDetail:
    'Optional five-week competition. Choose paper or real money, use one fixed starting balance, and trade with AI.',
  heading: 'Five-week trading competition',
  // Keep this short on the page. Details belong in the three points below.
  body: 'Join the optional five-week leaderboard with paper or real money. Everyone uses one fixed starting balance and trades with AI.',
  points: [
    'Trade your way: run a strategy you build in class, place your own trades with AI research, or let your AI agent trade for you.',
    'Each week of class hands you a new AI skill to try in the competition, so you practice on real market decisions instead of homework.',
    'One class leaderboard tracks participants. Best return at the end of week five wins.',
  ],
  img: '/images/courses/competition-banner.webp',
  imgAlt: 'Five-week trading competition with class leaderboard',
};

export const SEO_TOPICS = [
  'Agentic trading',
  'Claude',
  'AI stock analysis',
  'AI options trading',
  'Codex',
  'Algorithmic trading',
];

/**
 * What you receive. A plain list of what is in the box, kept separate from the
 * worth argument above. Only the recording library carries a price, and that
 * price comes from the live catalog because it is a product anyone can buy on
 * its own. Nothing else gets an invented number.
 */
export const buildValueStack = (selfPacedDisplay) => {
  const items = [
    {
      name: 'Five live sessions with Rob',
      detail: 'One to two hours each, your questions answered on camera.',
    },
    {
      name: 'Lifetime access',
      detail: 'Retake later cohorts as many times as you want. New material stays in your library.',
    },
    {
      name: 'Your own setup reviewed by Rob',
      detail: 'Bring what you built and he goes through it live.',
    },
    {
      name: 'Every prompt, checklist and script used in class',
      detail: 'The exact working material, not summaries of it.',
    },
    { name: COMPETITION.name, detail: COMPETITION.stackDetail },
  ];

  if (selfPacedDisplay) {
    items.push({
      name: 'The complete recording library, yours to keep',
      detail: 'This cohort, past classes, and future sessions we add to the portal.',
      value: selfPacedDisplay,
      valueNote: 'sold on its own as the self-paced course',
    });
  }

  return items;
};

/**
 * "What you'll learn" outcomes. Shared by the live and self-paced pages so the
 * two surfaces describe the same course. The self-paced buyer gets the same
 * lessons as recordings, so the outcomes are identical.
 */
export const DELIVERABLES = [
  {
    title: 'Your AI trading tools connected',
    detail: 'Claude, Codex, Cursor, and BotSpot all talking to your broker on night one.',
    img: '/images/courses/learn-tools-connected.webp',
  },
  {
    title: 'Stock research plus market scanners',
    detail:
      'Filings, financials, earnings, insider data, news, and a scanner that surfaces the next candidates.',
    img: '/images/courses/learn-research-scanners.webp',
  },
  {
    title: 'Options, crypto, and prediction-market trades',
    detail: 'Use Claude with TradingView, then place reviewed trades beyond stocks and ETFs.',
    img: '/images/courses/learn-multi-markets.webp',
  },
  {
    title: 'An AI agent that can trade for you every day',
    detail:
      'Research, decide, and place trades on a schedule inside guardrails you control. Under your command.',
    img: '/images/courses/learn-ai-agent.webp',
  },
  {
    title: 'Automation you can actually understand',
    detail:
      'Turn a plain-English setup into rules that run without babysitting every click, still with you approving the system.',
    img: '/images/courses/learn-understand-automation.webp',
  },
];

export const SKILLS = [
  'Agentic trading',
  'Claude',
  'Codex',
  'Cursor',
  'SEC filings',
  'Fundamental analysis',
  'Market scanners',
  'Claude with TradingView',
  'Pine Script',
  'Python strategies',
  'Backtesting',
  'Stocks and ETFs',
  'Options',
  'Crypto',
  'Polymarket',
  'AI trading agents',
  'Loop engineering',
];

/**
 * Real Trustpilot screenshots. Shared: social proof for the course applies to
 * both purchase paths. See "Reviews are king" in AGENTS.md before changing.
 */
export const REVIEW_SHOTS = [
  {
    src: '/images/reviews/tp-0.jpg',
    alt: 'Trustpilot review by Mauricio Rascon, five stars',
    tilt: -1.6,
    width: 530,
    height: 467,
  },
  {
    src: '/images/reviews/tp-3.jpg',
    alt: 'Trustpilot review by Worth, five stars',
    tilt: 1.2,
    width: 530,
    height: 489,
  },
  {
    src: '/images/reviews/tp-2.jpg',
    alt: 'Verified Trustpilot review by Daniel Ogola, five stars',
    tilt: -0.9,
    width: 530,
    height: 567,
  },
  {
    src: '/images/reviews/tp-6.jpg',
    alt: 'Trustpilot review by Charlie Murphy, five stars',
    tilt: 1.7,
    width: 530,
    height: 440,
  },
  {
    src: '/images/reviews/tp-4.jpg',
    alt: 'Trustpilot review by Robert Sanon, five stars',
    tilt: -1.3,
    width: 530,
    height: 399,
  },
  {
    src: '/images/reviews/tp-5.jpg',
    alt: 'Trustpilot review by Larry Ayala, five stars',
    tilt: 1.0,
    width: 530,
    height: 399,
  },
];

/** Lumiwealth YouTube student video reviews (playlist PL1ZXNE0ML97c6asc1BOzzh6rF2rqq61nw). */
export const VIDEO_REVIEWS = [
  { youtubeId: '5K40qQxUI3k', name: 'Joel', title: 'Joel video review' },
  { youtubeId: 'MDcXHBpdJ0I', name: 'Santayan', title: 'Santayan video review' },
  { youtubeId: 'gxl2ajPZLVk', name: 'Agim', title: 'Agim video review' },
  { youtubeId: 'H5HFWeCRqvw', name: 'LaToya', title: 'LaToya video review' },
  { youtubeId: '-VWa_Tf9V2k', name: 'Anthony', title: 'Anthony video review' },
  { youtubeId: 'lcBO3GcqLBc', name: 'Daniel', title: 'Daniel video review' },
];
